<template>
  <div class="container mx-auto px-5 md:px-0">
    <section class="flex flex-col md:flex-row items-center mt-6 md:mt-0">
      <div class="flex-1 w-full">
        <h1 class="uppercase font-bold text-xl md:text-3xl my-6 md:my-8 text-center md:text-left">
          {{ $t('contactUs') }}
        </h1>

        <address class="contact__address space-y-5 flex flex-col items-start mt-7 md:mt-0">
          <a class="flex items-center" :href="`mailto:${contact.email}`">
            <svg-icon-email width="24" height="24" />
            <span class="ml-3 text-base text-system-gray">{{ contact.email }}</span>
          </a>
          <a class="contact__address__landline flex items-center" :href="`tel:${contact.landLine1}`">
            <svg-icon-phone width="24" height="24" />
            <span class="ml-3 text-base text-system-gray">{{ contact.landLine1 }}</span>
          </a>
          <a class="contact__address__landline flex items-center" :href="`tel:${contact.landLine2}`">
            <svg-icon-phone width="24" height="24" />
            <span class="ml-3 text-base text-system-gray">{{ contact.landLine2 }}</span>
          </a>
          <a class="contact__address__landline flex items-center" :href="`tel:${contact.landLine3}`">
            <svg-icon-phone width="24" height="24" />
            <span class="ml-3 text-base text-system-gray">{{ contact.landLine3 }}</span>
          </a>
          <a
            v-if="contact.address"
            class="flex items-center md:col-span-2 lg:text-right lg:justify-end"
            :href="contact.location"
            target="_blank"
            rel="noreferrer"
          >
            <svg-icon-store-pickup width="24" height="24" />
            <span class="ml-3 text-base text-system-gray">{{ contact.address }}</span>
          </a>

          <p class="font-body text-primary-700">{{ $t('contactNotice') }}</p>
        </address>
      </div>
    </section>
    <section class="flex flex-col-reverse md:flex-row mt-10 md:mt-25 md:gap-x-25 mb-20 md:mb-23">
      <div class="flex-1 mt-10 md:mt-0">
        <picture class="w-full">
          <source srcset="/contact-us.avif" type="image/avif" />
          <source srcset="/contact-us.webp" type="image/webp" />
          <img src="/contact-us.jpg" alt="Contact Us" class="w-full h-full object-cover" />
        </picture>
      </div>
      <div class="flex-1">
        <h2 class="uppercase mt-12 text-lg md:text-xl text-primary-1-100 font-bold mb-5 md:mb-7">
          {{ $t('sendMessage') }}
        </h2>
        <form class="contact__form space-y-12 mt-12" @submit="onSubmit">
          <TextInput
            id="name"
            name="name"
            :label="$t('fullName')"
            type="text"
            rules="required"
            full-border
            required
            border
          />
          <TextInput
            id="email"
            name="email"
            :label="$t('email')"
            type="email"
            rules="required|email"
            autocomplete="email"
            full-border
            required
          />
          <TextInput
            id="subject"
            name="subject"
            :label="$t('subject')"
            type="text"
            rules="required"
            full-border
            required
          />
          <TextInput
            id="message"
            name="message"
            class="bg-transparent"
            :label="$t('message')"
            type="textarea"
            el="textarea"
            rules="required"
            full-border
            required
          />

          <AppButton class="mt-16 w-full sm:w-1/2 justify-center" :disabled="!meta.valid" inverted>
            <svg-icon name="send" class="mr-2 w-5 h-5 text-white transform -rotate-45" />
            <span class="text-center font-semibold whitespace-nowrap">{{ $t('submit') }}</span>
          </AppButton>
        </form>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { string, object } from 'yup';

const { input, submitForm } = useContactForm();
const { success, error } = useAlerts();
const formRef = ref<any>(null);
const { t } = useI18n();
const { t: $t } = useI18n({
  useScope: 'local',
});

const contact = {
  email: 'onlinesupport@mazaya.com.eg',
  address: t('address'),
  location: 'https://goo.gl/maps/x1RTmybGmuiwfH6JA',
  landLine1: '+20223066584',
  landLine2: '+20223066585',
  landLine3: '+20223066586',
};

const { handleSubmit, meta } = useForm({
  validationSchema: toTypedSchema(
    object({
      name: string().required(),
      email: string()
        .email()
        .required()
        .matches(/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/, t('validation.email')),
      subject: string().required(),
      message: string().required(),
    }),
  ),
});
const onSubmit = handleSubmit(async ({ name, email, subject, message }) => {
  try {
    input.name = name;
    input.email = email;
    input.reason_of_contact = subject;
    input.description = message;
    await submitForm();

    success(t('successTitle'), t('successSubtitle'));

    keysOf(input).forEach(key => {
      input[key] = '';
    });

    nextTick(() => {
      formRef.value?.reset();
    });
  } catch (err) {
    error(t('errorTitle'), t('errorSubtitle'));
  }
});

useSeoMeta({
  title: t('seo.title') as string,
  description: t('seo.description') as string,
});

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const items = [
  { label: t('home').toString(), path: '' },
  { label: t('seo.title').toString(), path: 'contact' },
];
</script>

<style lang="postcss" scoped>
.contact__grid {
  @screen lg {
    grid-template-columns: 60% 1fr;
  }
}

.contact__form {
  :deep(label) {
    @apply -top-7;
  }
}
.contact__address {
  &__landline span {
    unicode-bidi: plaintext;
  }
}

address {
  font-style: normal;
}
</style>

<i18n>
{
  "en": {
    "seo": {
      "title": "Contact us",
      "description": "Drop a message"
    },
    "contactInfo": "Contact Information",
    "address": "Sector 3 - New Cairo City - Plot No. 341 - 5th settlement  Northern Investors - Mohamed Naguib",
    "sendMessage": "Send Us a Message",
    "successTitle": "Message Sent!",
    "successSubtitle": "We will contact you shortly",
    "errorTitle": "Failed",
    "errorSubtitle": "Sorry something went wrong while sending the message, try again later",
    "fullName": "Full Name",
    "email": "Email",
    "subject": "Subject",
    "message": "Message",
    "submit": "SEND MESSAGE",
    "placeholders": {
      "message": "Add your message here",
      "subject": "Message subject"
    },
    "reachUs": "Reach Us",
    "contactUs": "Contact Us",
    "contactNotice": "Our working hours are from Saturday to Thursday from 9:00 a.m. to 3:00 p.m., excluding public holidays."
  },
  "ar": {
    "seo": {
      "title": "تواصل معنا",
      "description": "ارسل لنا رسالة"
    },
    "contactInfo": "معلومات التواصل",
    "address": "قطاع 3 - القاهرة الجديدة - قطعة رقم 341 - التجمع الخامس المستثمرين الشمالية - محمد نجيب",
    "sendMessage": "ارسل لنا رسالة",
    "contactUs": "تواصل معنا",
    "successTitle": "تم ارسال الرسالة",
    "successSubtitle": "سنتواصل معك قريبا",
    "errorTitle": "فشل الارسال",
    "errorSubtitle": "فشلت عمليه الارسال من فضلك حاول لاحقاً",
    "fullName": "الاسم بالكامل",
    "email": "البريد الإلكتروني",
    "subject": "الموضوع",
    "message": "الرسالة",
    "submit": "ارسل",
    "placeholders": {
      "message": "أضف رسالتك هنا",
      "subject": "موضوع الرسالة"
    },
    "reachUs": "تواصل معنا",
    "contactNotice": "ساعات العمل من السبت الى الخميس من الساعة 9:00 صباحاً الى 3:00 مساءً ما عدا العطلات الرسمية."
  }
}
</i18n>
